import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShieldAlertIcon, CodeIcon, FileTextIcon, DatabaseIcon, ArrowRightIcon } from 'lucide-react';
export function UseCasesPage() {
  const {
    role
  } = useParams<{
    role?: string;
  }>();
  const navigate = useNavigate();
  const roles = [{
    id: 'ciso',
    label: 'CISO & Security',
    icon: <ShieldAlertIcon className="h-5 w-5" data-id="element-642" />
  }, {
    id: 'developer',
    label: 'Engineering',
    icon: <CodeIcon className="h-5 w-5" data-id="element-643" />
  }, {
    id: 'compliance',
    label: 'Compliance & Audit',
    icon: <FileTextIcon className="h-5 w-5" data-id="element-644" />
  }, {
    id: 'data',
    label: 'Data Teams',
    icon: <DatabaseIcon className="h-5 w-5" data-id="element-645" />
  }];
  const useCasesData: Record<string, {
    title: string;
    subtitle: string;
    cases: {
      title: string;
      desc: string;
    }[];
  }> = {
    ciso: {
      title: 'Security Leadership',
      subtitle: 'Maintain absolute control and visibility over AI agent actions across your enterprise.',
      cases: [{
        title: 'Prevent Data Exfiltration',
        desc: 'Enforce real-time egress rules that stop AI agents from moving sensitive data outside of approved boundaries, regardless of the prompt.'
      }, {
        title: 'Zero Trust for AI',
        desc: 'Apply zero trust principles to autonomous agents. Agents must continuously prove their authorization context for every action they take.'
      }, {
        title: 'Blast Radius Containment',
        desc: 'Limit the potential damage of a compromised or hallucinating agent by enforcing strict, deterministic boundaries on what systems it can interact with.'
      }]
    },
    developer: {
      title: 'Engineering Teams',
      subtitle: 'Build powerful AI features without having to build complex authorization logic from scratch.',
      cases: [{
        title: 'Decoupled Authorization',
        desc: 'Remove complex auth logic from your agent code. Let MCPGuard handle the policy evaluation so you can focus on agent capabilities.'
      }, {
        title: 'Safe Production Deployments',
        desc: 'Move agents from dev to prod faster by giving security teams the deterministic controls they need to approve the deployment.'
      }, {
        title: 'Standardized Integration',
        desc: 'Use standard protocols to connect agents to internal tools without writing custom security wrappers for every new integration.'
      }]
    },
    compliance: {
      title: 'Compliance & Audit',
      subtitle: 'Generate the exact paper trails required by regulators for autonomous system actions.',
      cases: [{
        title: 'Full Chain Auditability',
        desc: 'Answer the "who, what, when, and why" for every AI action with cryptographically verifiable logs.'
      }, {
        title: 'Regulator-Defensible Scaling',
        desc: 'Prove to auditors that AI agents cannot bypass established access controls or data handling policies.'
      }, {
        title: 'Automated Redaction',
        desc: 'Ensure PII and PHI are automatically redacted from agent context windows before processing, maintaining GDPR/HIPAA compliance.'
      }]
    },
    data: {
      title: 'Data Engineering',
      subtitle: 'Protect your data stores from rogue queries while enabling AI-driven insights.',
      cases: [{
        title: 'Context Firewalling',
        desc: 'Inspect and filter queries generated by AI agents before they hit your production databases.'
      }, {
        title: 'Dynamic Tokenization',
        desc: 'Tokenize sensitive fields on the fly as data is returned to the agent, preserving analytical value while protecting privacy.'
      }, {
        title: 'Rate Limiting & Cost Control',
        desc: 'Prevent runaway agents from executing expensive or infinite query loops against your data infrastructure.'
      }]
    }
  };
  // If no role is selected, show the overview
  if (!role || !useCasesData[role]) {
    return <div className="w-full max-w-5xl mx-auto py-12 px-4 animate-in fade-in duration-500" data-id="element-646">
        <div className="text-center mb-16" data-id="element-647">
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4" data-id="element-648">
            Use Cases by Role
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-649">
            See how MCPGuard solves the unique challenges of deploying agentic
            AI for different teams across your organization.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-650">
          {roles.map(r => <Link key={r.id} to={`/use-cases/${r.id}`} className="group bg-white border border-gray-200 rounded-xl p-8 hover:border-[#4db8a4] hover:shadow-md transition-all" data-id="element-651">
              <div className="flex items-center gap-4 mb-4" data-id="element-652">
                <div className="h-12 w-12 bg-[#4db8a4]/10 rounded-lg flex items-center justify-center text-[#4db8a4] group-hover:bg-[#4db8a4] group-hover:text-white transition-colors" data-id="element-653">
                  {r.icon}
                </div>
                <h2 className="text-2xl font-bold text-[#1e3a5f]" data-id="element-654">{r.label}</h2>
              </div>
              <p className="text-gray-600 mb-6" data-id="element-655">
                {useCasesData[r.id].subtitle}
              </p>
              <div className="flex items-center text-[#4db8a4] font-medium group-hover:translate-x-1 transition-transform" data-id="element-656">
                Explore use cases <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-657" />
              </div>
            </Link>)}
        </div>
      </div>;
  }
  const data = useCasesData[role];
  return <div className="w-full max-w-5xl mx-auto py-12 px-4 animate-in fade-in duration-500" data-id="element-658">
      {/* Role Selector Nav */}
      <div className="flex flex-wrap gap-2 mb-12 border-b border-gray-200 pb-4" data-id="element-659">
        <button onClick={() => navigate('/use-cases')} className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-[#1e3a5f] transition-colors" data-id="element-660">
          All Roles
        </button>
        {roles.map(r => <button key={r.id} onClick={() => navigate(`/use-cases/${r.id}`)} className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${role === r.id ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-661">
            {r.label}
          </button>)}
      </div>

      <div className="mb-12" data-id="element-662">
        <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4" data-id="element-663">{data.title}</h1>
        <p className="text-xl text-gray-600 max-w-3xl" data-id="element-664">{data.subtitle}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8" data-id="element-665">
        {data.cases.map((c, idx) => <div key={idx} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-666">
            <div className="h-10 w-10 bg-[#e8f7f4] rounded-full flex items-center justify-center text-[#4db8a4] font-bold text-lg mb-4" data-id="element-667">
              {idx + 1}
            </div>
            <h3 className="text-xl font-bold text-[#1e3a5f] mb-3" data-id="element-668">{c.title}</h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-669">{c.desc}</p>
          </div>)}
      </div>
    </div>;
}